import { FC, useEffect, useState, useRef } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import classnames from 'classnames';
import { setAsTaxExempt } from '../../actions/checkoutSubmitActions';
import { trackTaxExempt } from '../../helpers/checkoutTracking';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { trackHasTaxDocsViewed, trackRemoveTaxClick } from '../../shared/helpers/taxExemptTracking';
import { EligibleForTaxExemptHeader } from '../../shared/helpers/taxExemptHelpers';

import styles from './styles/CheckoutTaxExemptionCheckbox.scss';

import { CheckoutTaxExemptionCheckbox_transaction$data } from './__generated__/CheckoutTaxExemptionCheckbox_transaction.graphql';
import { CheckoutTaxExemptionCheckbox_viewer$data } from './__generated__/CheckoutTaxExemptionCheckbox_viewer.graphql';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';

type Props = {
    transaction: CheckoutTaxExemptionCheckbox_transaction$data;
    isComputing: boolean;
    isTaxExempt: boolean;
    onSetAsTaxExempt: (isTaxExempt: boolean) => void;
    isMobile?: boolean;
    relay: RelayProp;
    viewer: CheckoutTaxExemptionCheckbox_viewer$data;
};

const CheckoutTaxExemptionCheckbox: FC<Props> = props => {
    const { viewer, isMobile, isTaxExempt, isComputing, onSetAsTaxExempt, transaction, relay } =
        props;
    const canBeTaxExempt = !!transaction?.canBeTaxExempt;
    const [isLoading, setIsLoading] = useState(false);
    const didMount = useRef(false);

    const updateTaxExemptReview = async (isChecked: boolean): Promise<void> => {
        try {
            setIsLoading(true);
            await CheckoutMutationWrapper.commit(
                'TransactionTaxExemptReview',
                relay.environment,
                {
                    transactionId: transaction?.serviceId,
                    isChecked,
                },
                false
            );
        } catch (e) {
            logCheckoutError(e, 'taxexemption');
        }
        onSetAsTaxExempt(isChecked);
        setIsLoading(false);
    };

    useEffect(() => {
        if (canBeTaxExempt) {
            trackTaxExempt({
                label: 'tax exempt checkbox displayed',
            });
            trackHasTaxDocsViewed('checkout');
        }
    }, [canBeTaxExempt]);

    useEffect(() => {
        if (
            canBeTaxExempt &&
            transaction?.reviews &&
            viewer?.isMultiStageCheckoutOn &&
            !didMount.current
        ) {
            didMount.current = true;
            const lastTaxExemptReview = [...transaction?.reviews]
                .reverse()
                .find(e => e?.status === 'APPROVED' && e?.type === 'TAX_EXEMPTION');

            if (lastTaxExemptReview) {
                onSetAsTaxExempt(!!lastTaxExemptReview);
            }
        }
    }, [canBeTaxExempt, transaction, viewer, onSetAsTaxExempt]);

    if (!canBeTaxExempt && !isComputing) {
        return null;
    }

    return (
        <div className={isMobile ? styles.wrapper : ''}>
            <div
                className={classnames(styles.eligibleForTaxExemption, {
                    [styles.isMobile]: isMobile,
                })}
            >
                *
                <EligibleForTaxExemptHeader />
            </div>
            <Checkbox
                name="isTaxExempt"
                checked={!!isTaxExempt}
                disabled={isLoading}
                onChange={checked => {
                    trackRemoveTaxClick('checkout');
                    if (viewer?.isMultiStageCheckoutOn) {
                        updateTaxExemptReview(checked);
                    } else {
                        onSetAsTaxExempt(checked);
                    }
                }}
                dataTn="tax-exempt"
                label={
                    <FormattedMessage
                        id="checkout.CheckoutTaxExemptionCheckbox.saveAmountSalesTax"
                        defaultMessage="Please check this box to certify that this purchase is tax exempt."
                    />
                }
                labelClass={classnames(styles.label, {
                    [styles.isMobile]: isMobile,
                })}
                wrapperClass={styles.checkbox}
            />
        </div>
    );
};

type ReduxPayload = {
    transaction: {
        isTaxExempt: boolean;
        serviceId: string;
    };
};

const ConnectedCheckoutTaxExemptionCheckbox = connect(
    ({ transaction }: ReduxPayload) => {
        return {
            isTaxExempt: transaction.isTaxExempt,
            transactionId: transaction.serviceId,
        };
    },
    {
        onSetAsTaxExempt: setAsTaxExempt,
    }
)(CheckoutTaxExemptionCheckbox);

export default createFragmentContainer(ConnectedCheckoutTaxExemptionCheckbox, {
    viewer: graphql`
        fragment CheckoutTaxExemptionCheckbox_viewer on Viewer {
            isMultiStageCheckoutOn: featureFlag(feature: "multiStageCheckout")
        }
    `,
    transaction: graphql`
        fragment CheckoutTaxExemptionCheckbox_transaction on Transaction {
            id
            serviceId
            canBeTaxExempt
            reviews {
                type
                status
            }
        }
    `,
});
